<template>
  <v-dialog
    v-model="opened"
    content-class="expense-edit"
    max-width="400"
    persistent
  >
    <h3 class="expense-edit__title">
      {{ $t('workersList.expenseEdit') }}
    </h3>

    <v-btn class="add-btn" color="success" icon @click="addTransaction">
      <v-icon>mdi-plus</v-icon>
    </v-btn>

    <v-btn class="close-btn" icon @click="opened = false">
      <v-icon size="27" color="#464646"> mdi-close-circle-outline </v-icon>
    </v-btn>

    <v-progress-linear
      v-if="itemLoading"
      style="border-radius:3px"
      color="warning"
      height="6"
      indeterminate
    />

    <transition name="fadeHeight">
      <v-form
        v-if="!itemLoading"
        ref="expenseEdit"
        :disabled="loading"
        @submit.prevent="onSubmit"
      >
        <div class="expense-edit__list">
          <v-expansion-panels v-model="expensesPanels" multiple>
            <v-expansion-panel
              v-for="{ value, key, customField } in expensesTypes"
              :key="key"
            >
              <v-expansion-panel-header
                :disable-icon-rotate="!!formData.types[`${key}-amount`]"
              >
                <template v-if="hasErrors(key)" v-slot:actions>
                  <v-icon class="mr-1" color="error" small>
                    mdi-alert-circle
                  </v-icon>
                </template>
                <template
                  v-else-if="formData.types[`${key}-amount`]"
                  v-slot:actions
                >
                  <v-icon class="mr-1" color="success" small>
                    mdi-checkbox-marked-circle
                  </v-icon>
                </template>

                <div v-if="customField" class="expense-edit__custom-field">
                  <v-btn
                    class="mr-2"
                    icon
                    color="red"
                    small
                    @click="deleteTransaction(key)"
                  >
                    <v-icon size="18" color="red"> mdi-delete-outline </v-icon>
                  </v-btn>

                  <span v-if="formData.types[`${key}-name`]">
                    {{ formData.types[`${key}-name`] }}
                  </span>

                  <i v-else class="expense-edit__enter-name">
                    * {{ $t('roles.search') }} *
                  </i>
                </div>
                <span v-else>{{ value }}</span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-text-field
                  v-if="customField"
                  v-model="formData.types[`${key}-name`]"
                  :ref="`name-${key}`"
                  class="mb-2"
                  outlined
                  dense
                  hide-details
                  :label="$t('companiesList.tableHeaders.name')"
                  :rules="[isRequired]"
                />
                <div class="d-flex">
                  <v-text-field
                    v-model="formData.types[`${key}-amount`]"
                    :ref="`amount-${key}`"
                    class="mr-1"
                    outlined
                    dense
                    hide-details
                    :label="$t('common.fields.sum')"
                    :rules="getAmountRules(customField)"
                  />
                  <v-text-field
                    v-model="formData.types[`${key}-comment`]"
                    class="ml-1"
                    outlined
                    dense
                    hide-details
                    :label="$t('common.fields.comment')"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>

        <v-btn
          class="mt-4"
          color="warning"
          type="submit"
          elevation="0"
          block
          :loading="loading"
        >
          {{ $t('common.btns.save') }}
        </v-btn>
      </v-form>
    </transition>
  </v-dialog>
</template>

<script>
export default {
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    loading: false,
    itemLoading: false,
    transactions: [],
    expensesPanels: [],
    expensesTypes: [],
    formData: {
      types: {},
    },
  }),
  watch: {
    opened(val) {
      if (val) {
        const { expensesTypes } = this.$store.state['workers-board'];

        this.expensesTypes = expensesTypes.filter(({ key }) => key !== '29');
        this.fetchItemData();
      } else {
        this.$refs.expenseEdit.reset();
        this.loading = false;
        this.expensesTypes = [];
        this.expensesPanels = [];
        this.formData.types = {};
        setTimeout(() => {
          this.itemLoading = true;
        }, 300);
      }
    },
  },
  computed: {
    opened: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('change', val);
      },
    },
    incomeTransactions() {
      return this.transactions.filter(
        (transaction) => transaction.is_number_positive,
      );
    },
    expenseTransactions() {
      return this.transactions.filter(
        (transaction) => !transaction.is_number_positive,
      );
    },
    requestData() {
      let transactions = Object.entries(this.formData.types).reduce(
        (res, [key, value]) => {
          if (!value) return res;

          const [id, param] = key.split('-');
          const transaction = res.find((item) => item.type === id);

          if (transaction) {
            transaction[param] = value;
          } else {
            res.push({
              type: id,
              [param]: value,
            });
          }

          return res;
        },
        [],
      );

      transactions = transactions.map((item) => {
        if ('name' in item) {
          return {
            ...item,
            type: '29',
          };
        }

        return item;
      });

      return {
        car_id: this.item.car_id,
        driver_id: this.item.driver_id,
        transactions: [...this.incomeTransactions, ...transactions],
      };
    },
  },
  methods: {
    isRequired(v) {
      const isValid = Boolean(v) || v === 0;
      return isValid || this.$t('common.fields.validation.field.required');
    },
    onlyNumbers(v) {
      if (!v) return true;
      return (
        /^[0-9]{1,12}$/.test(v) || this.$t('common.fields.validation.numbers')
      );
    },
    getAmountRules(isCustom) {
      const rules = [this.onlyNumbers];

      if (isCustom) rules.push(this.isRequired);

      return rules;
    },
    hasErrors(key) {
      const amounts = this.$refs[`amount-${key}`];
      const names = this.$refs[`name-${key}`];
      let amountInvalid = false;
      let nameInvalid = false;

      if (amounts) {
        amountInvalid = amounts.some(
          ({ validationState }) => validationState === 'error',
        );
      }

      if (names) {
        nameInvalid = names.some(
          ({ validationState }) => validationState === 'error',
        );
      }

      return amountInvalid || nameInvalid;
    },
    async fetchItemData() {
      this.itemLoading = true;
      const { transactions } = await this.$store.dispatch(
        'workers-board/fetchListItem',
        this.item.id,
      );

      this.transactions = transactions;
      this.itemLoading = false;
      this.fillForm();
    },
    fillForm() {
      this.expenseTransactions.forEach((item, index) => {
        let { type } = item;

        if (type === 29) {
          type += index; // unique type is required
          this.formData.types[`${type}-name`] = item.name;
          this.expensesTypes = [
            { key: type, customField: true },
            ...this.expensesTypes,
          ];
        }

        this.formData.types[`${type}-amount`] = Number(item.amount);
        this.formData.types[`${type}-comment`] = item.comment;
        this.formData.types[`${type}-id`] = item.id;
      });

      this.expensesTypes.forEach((obj, idx) => {
        if (this.formData.types[`${obj.key}-amount`]) {
          this.expensesPanels.push(idx);
        }
      });
    },
    addTransaction() {
      const expensesKeys = this.expensesTypes.map(({ key }) => key);
      const lastKey = Math.max(...expensesKeys);
      const newField = {
        key: lastKey + 1,
        customField: true,
      };

      this.expensesPanels.unshift(this.expensesTypes.length);
      this.expensesTypes = [newField, ...this.expensesTypes];
    },
    deleteTransaction(key) {
      const idx = this.expensesTypes.findIndex((item) => item.key === key);
      this.expensesTypes = this.expensesTypes.filter(
        (item) => item.key !== key,
      );

      this.expensesPanels = this.expensesPanels.map((index) => {
        if (index > idx) return index - 1;
        return index;
      });

      Object.keys(this.formData.types).forEach((param) => {
        const [paramKey] = param.split('-');

        if (Number(paramKey) === key) {
          delete this.formData.types[param];
        }
      });
    },
    async onSubmit() {
      const isValid = this.$refs.expenseEdit.validate();

      if (!isValid) return;

      this.loading = true;

      const res = await this.$store.dispatch('workers-board/updateItem', {
        id: this.item.id,
        formData: this.requestData,
      });

      this.$emit('updated', { res, id: this.item.id });
    },
  },
};
</script>

<style lang="scss">
.expense-edit {
  padding: 10px 15px;
  overflow-y: auto;
  background-color: #eee;

  &__custom-field {
    display: flex;
    align-items: center;
  }

  &__enter-name {
    color: #777;
  }

  &__title {
    color: #484848;
    font-size: 18px;
    margin-bottom: 15px;
  }

  &__list {
    height: 300px;
    overflow-y: auto;
  }

  .v-expansion-panel-header {
    padding: 10px 15px;
    min-height: 35px;
  }

  .v-expansion-panel-content__wrap {
    padding: 10px 15px;

    .v-label,
    .v-select__selection,
    input {
      font-size: 14px;
    }
  }
}

.fadeHeight-enter-active,
.fadeHeight-leave-active {
  transition: all 0.2s;
  max-height: 370px;
}
.fadeHeight-enter,
.fadeHeight-leave-to {
  opacity: 0;
  max-height: 0px;
}
</style>
